<template>
  <layout-waiter :nav-menu-items="navMenuItems">
    <slot v-if="!isLoading" />

    <!-- Slot: Navbar -->
    <template #navbar="{ toggleVerticalNavMenuActive }">
      <div class="navbar-content-container">
        <!-- Left Content: -->
        <div class="d-flex align-center">
          <v-icon
            v-if="$vuetify.breakpoint.mdAndDown"
            class="me-3"
            @click="toggleVerticalNavMenuActive"
          >
            {{ icons.mdiMenu }}
          </v-icon>

          <v-btn
            v-if="['staging', 'development'].includes(currentEnv)"
            color="error"
            :ripple="false"
            small
          >
            {{
              {
                staging: 'STG',
                development: 'DEV'
              }[currentEnv]
            }}
          </v-btn>
        </div>

        <!-- Right Content: -->
        <div class="d-flex align-center right-row">
          <clock class="mr-4" />
          <v-btn
            icon
            depressed
            :ripple="false"
            :loading="isLoading"
            @click="$router.go(0)"
          >
            <v-icon>
              {{ icons.mdiReload }}
            </v-icon>
          </v-btn>
          <app-bar-theme-switcher class="mr-2" />
          <app-bar-notification
            :notifications="currentClubNotifications"
            :color="bellColor"
            class="mr-2"
            @update:close-notification-card="setColorEmpty"
          />
          <monitor-menu class="mr-5" />
          <cash-register-menu />
        </div>
      </div>
    </template>

    <!-- Slot: Footer -->
    <template #footer>
      <!-- TODO -->
    </template>
  </layout-waiter>
</template>

<script>
// App Bar Components
import AppBarThemeSwitcher from '@core/layouts/components/app-bar/AppBarThemeSwitcher.vue'
import AppBarNotification from '@core/layouts/components/app-bar/WaiterAppBarNotification.vue'
import MonitorMenu from '@core/layouts/components/app-bar/MonitorMenu.vue'

import { mdiMenu, mdiHeartOutline, mdiReload } from '@mdi/js'

import CashRegisterMenu from '@core/layouts/components/app-bar/CashRegisterMenu.vue'

import {
  reactive,
  provide,
  getCurrentInstance,
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
} from '@vue/composition-api'

import ClubApi from '@/api/waiter/Club'
import ReportApi from '@/api/waiter/Report'
import ReceiptNotePresetApi from '@/api/v2/utility/settings/ReceiptNotePreset'
import CustomerTagApi from '@/api/waiter/CustomerTag'
import MonitorTimecardApi from '@/api/v2/monitor/Timecard'
import MonitorTableApi from '@/api/v2/monitor/Table'
import MonitorVendingHistoryApi from '@/api/v2/monitor/VendingHistory'
import MonitorKpiApi from '@/api/v2/monitor/Kpi'

import buildNavMenuItems from '@/navigation/waiter'
import LayoutWaiter from '@/@core/layouts/variants/LayoutWaiter.vue'

import Clock from '@/views/components/util/Clock.vue'
import useDateFormat from '@/views/composable/useDateFormat'
import { cable } from '@/plugins/websocket-client'

import useReceiptPrinterConnection from '@/views/composable/useReceiptPrinterConnection'
import useCompInit from '@/views/composable/useCompInit'
import useAudioPlayer from '@/views/composable/useAudioPlayer'
import useKitchenPrintWatcher from '@/views/composable/useKitchenPrintWatcher'

export default {
  components: {
    LayoutWaiter,

    // App Bar Components
    AppBarThemeSwitcher,
    AppBarNotification,

    Clock,
    CashRegisterMenu,
    MonitorMenu,
  },
  setup() {
    const vm = getCurrentInstance().proxy
    const navMenuItems = computed(() => buildNavMenuItems())

    const { isLoading, initWith } = useCompInit()
    const { dateTime } = useDateFormat()
    const currentClubMeta = reactive({
      remainingCashRegisterMoney: 0,
      todaySalesUnfixed: 0,
      todaySales: 0,
      today: new Date().toISOString().substr(0, 10),
    })
    const currentClubNotifications = ref([])
    const bellColor = ref('')
    const {
      isConnected,
      isConnecting,
      printer,
      checkConnection,
    } = useReceiptPrinterConnection()

    // AudioPlayer: iOS/iPadOS向け通知音アンロック機構
    const {
      setupUnlockListeners,
      preloadSounds,
      playSound,
    } = useAudioPlayer()
    const receiptNotePresets = ref([])
    const customerTags = ref([])
    const currentStats = reactive({
      castCount: 0,
      tableCount: 0,
      customerCount: 0,
      castWorkingTime: 0,
      customerUsageTime: 0,
    })
    const kpiStats = reactive({
      customerCount: 0,
      salesAmount: 0,
      customKpis: [],
    })

    const getClub = async () => {
      const res = await ClubApi.getClub()

      if (res?.data?.data) {
        const {
          remainingCashRegisterMoney,
          todaySalesUnfixed,
          todaySales,
          today,
        } = res.data.data.meta

        currentClubMeta.remainingCashRegisterMoney = remainingCashRegisterMoney
        currentClubMeta.todaySalesUnfixed = todaySalesUnfixed
        currentClubMeta.todaySales = todaySales
        currentClubMeta.today = today
      }
    }

    const getReceiptNotePresets = async () => {
      try {
        const res = await ReceiptNotePresetApi.getReceiptNotePresets()
        receiptNotePresets.value = res.data.data.data
      } catch (error) {
        vm.$toast.error(error.response.data.message.join('\n'))
      }
    }

    const getActiveCastCount = async () => {
      try {
        const res = await MonitorTimecardApi.getActiveCastCount()
        currentStats.castCount = res.data.data
      } catch (error) {
        vm.$toast.error(error.response.data.message.join('\n'))
      }
    }
    const getActiveTableCounts = async () => {
      try {
        const res = await MonitorTableApi.getActiveTableCounts()
        const { tableCount, customerCount } = res.data.data
        currentStats.tableCount = tableCount
        currentStats.customerCount = customerCount
      } catch (error) {
        vm.$toast.error(error.response.data.message.join('\n'))
      }
    }
    const fetchPlusMinusStats = async () => {
      await Promise.all([getActiveCastCount(), getActiveTableCounts()])
    }

    const getCastWorkingTime = async () => {
      try {
        const res = await MonitorTimecardApi.getCastWorkingTime()
        currentStats.castWorkingTime = res.data.data
      } catch (error) {
        vm.$toast.error(error.response.data.message.join('\n'))
      }
    }
    const getCustomerUsageTime = async () => {
      try {
        const res = await MonitorVendingHistoryApi.getCustomerUsageTime()
        currentStats.customerUsageTime = res.data.data
      } catch (error) {
        vm.$toast.error(error.response.data.message.join('\n'))
      }
    }
    // 稼働率の情報を取得
    const fetchOperationRateStats = async () => {
      await Promise.all([getCastWorkingTime(), getCustomerUsageTime()])
    }

    // KPI情報を取得
    const fetchKpiStats = async () => {
      try {
        const res = await MonitorKpiApi.getKpis()
        const { customer_count, sales_amount, custom_kpis } = res.data.data
        kpiStats.customerCount = customer_count
        kpiStats.salesAmount = sales_amount
        kpiStats.customKpis = custom_kpis
      } catch (error) {
        vm.$toast.error(error.response?.data?.message?.join('\n') || 'KPIの取得に失敗しました')
      }
    }

    const getCustomerTags = async () => {
      const res = await CustomerTagApi.getCustomerTags()
      if (res?.data) {
        customerTags.value = [...res.data.customerTags.data]
      }
    }

    const getReport = async () => {
      const res = await ReportApi.getReports()
      if (res?.data?.data) {
        currentClubNotifications.value = res.data.data.map(report => ({ ...report.attributes.report, time: dateTime(report.attributes.report.time) }))
      }
    }

    // websocketストアにNotificationChannel登録
    vm.$store.commit('websocket/REGISTER_CHANNEL', 'notification')

    const subscription = cable.subscriptions.create(
      {
        channel: 'NotificationChannel',
      },
      {
        connected: () => {
          vm.$store.dispatch('websocket/channelConnected', 'notification')
        },
        disconnected: () => {
          vm.$store.dispatch('websocket/channelDisconnected', 'notification')
        },
        received: ({
          title,
          time,
        }) => {
          bellColor.value = 'success'
          currentClubNotifications.value.unshift({ title, time: dateTime(time) })
        },
      },
    )

    // NotificationChannel再接続時にNotificationデータを再取得
    const notificationConnected = computed(() => vm.$store.state.websocket.channels.notification?.connected)
    let prevNotificationConnected = null
    watch(notificationConnected, newVal => {
      if (prevNotificationConnected === false && newVal === true) {
        getReport().catch(err => console.error('[WebSocket] Failed to refetch notifications:', err))
      }
      prevNotificationConnected = newVal
    })

    const setColorEmpty = () => {
      bellColor.value = ''
    }

    checkConnection()

    initWith([
      getClub(),
      getReceiptNotePresets(),
      getReport(),
      getCustomerTags(),
      fetchPlusMinusStats(),
      fetchOperationRateStats(),
      fetchKpiStats(),
    ])

    // キッチンプリンター印刷: モバイルオーダー注文時（重複印刷防止付き）
    useKitchenPrintWatcher()

    // SelfOrderChannel接続 & AudioPlayer初期化
    onMounted(async () => {
      // iOS/iPadOS向けAudioPlayerアンロック機構を設定
      setupUnlockListeners()
      await preloadSounds()

      // audioPlayerをstoreに設定
      vm.$store.commit('selfOrder/SET_AUDIO_PLAYER', { playSound })

      // SelfOrderChannel接続
      vm.$store.dispatch('selfOrder/connect')
    })

    onBeforeUnmount(() => {
      vm.$store.dispatch('selfOrder/disconnect')
    })

    provide('currentClubMeta', currentClubMeta)
    provide('getClub', getClub)
    provide('isReceiptPrinterConnected', isConnected)
    provide('isReceiptPrinterConnecting', isConnecting)
    provide('printer', printer)
    provide('noticeSubscription', subscription)
    provide('receiptNotePresets', receiptNotePresets)
    provide('customerTags', customerTags)
    provide('currentStats', currentStats)
    provide('fetchPlusMinusStats', fetchPlusMinusStats)
    provide('fetchOperationRateStats', fetchOperationRateStats)
    provide('kpiStats', kpiStats)
    provide('fetchKpiStats', fetchKpiStats)

    return {
      // data
      navMenuItems,
      isLoading,
      bellColor,
      currentClubNotifications,
      currentStats,

      // methods
      setColorEmpty,

      icons: {
        mdiMenu,
        mdiHeartOutline,
        mdiReload,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.navbar-content-container {
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-grow: 1;
  position: relative;
}

// ? Handle bg of autocomplete for blured appBar
.v-app-bar.bg-blur {
  .expanded-search {
    ::v-deep .app-bar-autocomplete-box div[role='combobox'] {
      background-color: transparent;
    }

    > .d-flex > button.v-icon {
      display: none;
    }

    // ===

    & > .right-row {
      visibility: hidden;
      opacity: 0;
    }
  }
}
</style>
